import {
  ClientStatusAction,
  ClientStatusChange,
  ClientStatusFlags,
  DeleteReason,
} from "@/data/models/ClientAdministrationRow";

/**
 * The Status card on a client's detail in the IRES Admin Client (#2610067): Gesperrt, Aktiv / Inaktiv,
 * Löschen / Wiederherstellen. Pure; IRES Cloud repeats every rule (`setClientStatusForAdministration`,
 * IRES Cloud `docs/apis/list-clients-for-administration.md`).
 */

type Translate = (key: string, values?: unknown[]) => string;

/** What the Status dialog edits. */
export type StatusDialogMode = "blocked" | "active" | "delete" | "restore";

/** A note's longest length, as IRES Cloud allows it. */
export const STATUS_NOTE_MAX_LENGTH = 200;

/** What stands in the way of deleting or deactivating, as IRES Cloud names it. */
export type StatusCondition =
  | "DEVICES_ASSIGNED"
  | "RUNNING_PROJECTS"
  | "SHARED_USERS_AT_HOME"
  | "MASTER_WITH_MEMBERS"
  | "ERP_CONNECTED";

const PREFIX = "admin.clients.status";

/**
 * The dialog's state. `choice` is the radio's value (Gesperrt / Aktiv), unused for delete and restore.
 */
export interface StatusDialogState {
  mode: StatusDialogMode;
  current: ClientStatusFlags;
  choice: boolean;
  reason: DeleteReason | null;
  note: string;
}

/** The action a dialog state saves. */
export function statusActionOf(state: StatusDialogState): ClientStatusAction {
  switch (state.mode) {
    case "blocked":
      return state.choice ? "BLOCK" : "UNBLOCK";
    case "active":
      return state.choice ? "ACTIVATE" : "DEACTIVATE";
    case "delete":
      return "DELETE";
    case "restore":
      return "RESTORE";
  }
}

/** The radio's starting value: the stored flag. */
export function initialChoice(mode: StatusDialogMode, current: ClientStatusFlags): boolean {
  return mode === "active" ? current.isActive : current.blocked;
}

/** Whether the note field applies: when blocking, and when deleting. */
export function takesNote(state: StatusDialogState): boolean {
  return state.mode === "delete" || (state.mode === "blocked" && state.choice && !state.current.blocked);
}

/** Whether anything was entered, so ABBRECHEN must ask (edit-mode-dialogs.md). */
export function hasStatusChanged(state: StatusDialogState): boolean {
  switch (state.mode) {
    case "blocked":
      return state.choice !== state.current.blocked || state.note.trim() !== "";
    case "active":
      return state.choice !== state.current.isActive;
    case "delete":
      return state.reason !== null || state.note.trim() !== "";
    case "restore":
      return false;
  }
}

/**
 * Whether SICHERN / LÖSCHEN / WIEDERHERSTELLEN may be pressed: the flag must change, a deletion needs a
 * reason (and a note for Sonstiges), and a note fits IRES Cloud's limit.
 */
export function canSaveStatus(state: StatusDialogState): boolean {
  const note = state.note.trim();
  if (note.length > STATUS_NOTE_MAX_LENGTH) {
    return false;
  }
  switch (state.mode) {
    case "blocked":
      return state.choice !== state.current.blocked;
    case "active":
      return state.choice !== state.current.isActive;
    case "delete":
      return !state.current.deleted && state.reason !== null && (state.reason !== "OTHER" || note !== "");
    case "restore":
      return state.current.deleted;
  }
}

/** The note as sent: trimmed, null when empty or not taken. */
export function noteToSend(state: StatusDialogState): string | null {
  const note = state.note.trim();
  return takesNote(state) && note !== "" ? note : null;
}

/** A refusal from IRES Cloud the dialog explains itself; anything else is a failed save. */
export type StatusRefusal =
  | { kind: "CONDITIONS_NOT_MET"; conditions: StatusCondition[] }
  | { kind: "CHANGED_MEANWHILE" }
  | { kind: "FAILED" };

/** Reads IRES Cloud's refusal from a callable error. */
export function statusRefusalOf(error: unknown): StatusRefusal {
  const details = (error as { details?: { reason?: unknown; conditions?: unknown } } | null)?.details;
  if (details?.reason === "CHANGED_MEANWHILE") {
    return { kind: "CHANGED_MEANWHILE" };
  }
  if (details?.reason === "CONDITIONS_NOT_MET" && Array.isArray(details.conditions)) {
    return {
      kind: "CONDITIONS_NOT_MET",
      conditions: details.conditions.filter((condition): condition is StatusCondition => typeof condition === "string"),
    };
  }
  return { kind: "FAILED" };
}

/** The i18n key of an unmet condition, as the dialog lists it. */
export function statusConditionKey(condition: StatusCondition): string {
  return `${PREFIX}.conditions.${condition}`;
}

/** A delete reason's label. */
export function deleteReasonLabel(reason: DeleteReason, t: Translate): string {
  return t(`${PREFIX}.deleteReasons.${reason}`);
}

/**
 * "geändert am 15.10.2026 von Max Muster" under a flag, the same wording as Betrieb; null when no
 * change is recorded (every flag set before #2610067).
 */
export function statusChangeText(
  change: ClientStatusChange | null | undefined,
  formatDate: (date: Date) => string,
  t: Translate
): string | null {
  if (!change || change.changedAtMillis === null) {
    return null;
  }
  const date = formatDate(new Date(change.changedAtMillis));
  return change.changedByName
    ? t("admin.clients.liveOperation.changedAtBy", [date, change.changedByName])
    : t("admin.clients.liveOperation.changedAt", [date]);
}
